/**
 * O painel das seções do painel principal.
 *
 * É o `FeatureSection` de `lib/features/dashboard/presentation/widgets/
 * feature_section.dart`: vidro em degradê, o fio de luz atravessando o topo, a
 * pastilha do ícone na cor da seção, título em Cormorant e legenda miúda. Cada
 * prévia do painel do Flutter mora dentro de um destes, então ele vem antes
 * delas — sem ele, cada prévia inventaria a própria moldura e o painel viraria
 * uma colcha de retalhos.
 *
 * Quando tem `para`, o painel inteiro é o link, como o `onTap` de lá.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { NomeDoIcone } from '@nosso-sempre/shared';
import { Icone } from './Icone';
import './secao.css';

interface Props {
  icone: NomeDoIcone;
  /** A cor de destaque da seção, o `hue` de lá. */
  cor: string;
  titulo: string;
  legenda?: string;
  /** A pastilha colada no título, o `ShelfHeader.badge` de lá: hoje só o
   *  "NOVO" da prateleira de anime vazia. */
  marca?: string;
  /** O caminho que o painel abre. Sem ele, a seção não é clicável. */
  para?: string;
  /** O canto direito do cabeçalho: a setinha, um contador, um botão. */
  ponta?: ReactNode;
  children: ReactNode;
}

export function Secao({ icone, cor, titulo, legenda, marca, para, ponta, children }: Props) {
  const miolo = (
    <>
      <span className="secao-fio" aria-hidden="true" />
      <div className="secao-topo">
        <span className="secao-selo" aria-hidden="true">
          <Icone nome={icone} tamanho={20} />
        </span>
        <div className="secao-dizeres">
          <h2 className="secao-titulo">
            {titulo}
            {marca ? <span className="secao-marca">{marca}</span> : null}
          </h2>
          {legenda ? <p className="secao-legenda">{legenda}</p> : null}
        </div>
        {ponta}
      </div>
      <div className="secao-miolo">{children}</div>
    </>
  );

  const estilo = { ['--cor-secao' as string]: cor };

  if (para) {
    return (
      <Link className="secao aberta" to={para} style={estilo}>
        {miolo}
      </Link>
    );
  }

  return (
    <section className="secao" style={estilo}>
      {miolo}
    </section>
  );
}

/** A setinha do canto, o `SectionChevron` de lá. */
export function SetaDaSecao() {
  return (
    <span className="secao-seta" aria-hidden="true">
      <Icone nome="avancar" tamanho={18} />
    </span>
  );
}
